/* eslint-disable import/first */
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import axios from 'axios';
import { UserPlus, Settings, CheckCircle, Save, AlertCircle, Plus, Users, Search, ArrowLeft, FileText, Activity, Pill, Trash2, Printer, Receipt, LayoutDashboard, Wallet, TrendingUp, CalendarDays, Clock, Check, Image as ImageIcon, Upload, Brain } from 'lucide-react';
import PrescriptionForm from './components/PrescriptionForm';
import TreatmentSection from './components/TreatmentSection';
import NotesSection from './components/NotesSection';
import { theme, navItem, cardStyle, inputStyle, btnStyle, settingBox, labelStyle, tdStyle, thStyle, statCard } from './theme';
import { INITIAL_PATIENT, STATUS_CONFIG, CAL_SLOTS } from './constants';
import LoginPage from './pages/LoginPage';
import ClinicSignupPage from './pages/ClinicSignupPage';
import DashboardPage from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import AppointmentsPage from './pages/AppointmentsPage';
import RegisterPatientPage from './pages/RegisterPatientPage';
import PatientsPage from './pages/PatientsPage';
import Sidebar from './layout/Sidebar';
import Header from './layout/Header';
import { NAV_ITEMS } from './layout/Sidebar';
import SlotBookingModal from './components/modals/SlotBookingModal';
import GlobalSearch from './components/GlobalSearch';
const HelpPage = lazy(() => import('./pages/HelpPage'));
// 4 staff stubs (leaves/payslips/incentives/advances) → PayrollHubPage.
// Certificates + StaffTraining → CredentialsHubPage.
// StaffTasks + StaffOnboarding → ChecklistsHubPage.
import PayrollHubPage from './pages/PayrollHubPage';
import CredentialsHubPage from './pages/CredentialsHubPage';
import ChecklistsHubPage from './pages/ChecklistsHubPage';
// CouponsPage stub removed — `coupons` route now uses real CouponManagerPage.
// Other Finance stubs (Estimates/Ledger/CreditNotes/RecurringCharges) kept
// for now — BillingPage doesn't have equivalent tabs yet.
import { EstimatesPage, LedgerPage, CreditNotesPage, RecurringChargesPage } from './pages/FinancePagesBundle';
// InventoryPagesBundle removed — those were GenericCrudPage stubs that
// duplicated the real Supply Chain UI inside PharmacyPage's "advanced" tab.
// Routes purchase-orders / grns / stock-adjustments / stock-transfers /
// pharmacy-suppliers now redirect to PharmacyPage with the matching
// initialTab so users land on the real PharmacyAdvancedPanel.
// ClinicalPagesBundle stubs (Ortho/Perio/Implants/Education) removed —
// duplicates of ClinicalPage internal tabs. PrescriptionsPage kept for
// now since ClinicalPage has no Rx tab yet.
import { PrescriptionsPage } from './pages/ClinicalPagesBundle';
// AiImaging / Dslr stubs now consolidated under ImagingHubPage (with Radiographs).
import { LeadsPage, OneDriveBackupPage, SupplierAnalyticsPage } from './pages/AdvancedPagesBundle';
import ImagingHubPage from './pages/ImagingHubPage';
import { ClinicalHubPage, BillingHubPage, PharmacyHubPage, StaffHubPage, MarketingHubPage,
  CLINICAL_CARDS, BILLING_CARDS, PHARMACY_CARDS, STAFF_CARDS, MARKETING_CARDS } from './pages/HubPagesBundle';
import SaasBillingPage from './pages/SaasBillingPage';
import RegistrationFormPrint from './components/RegistrationFormPrint';
import { BranchProvider, useBranch } from './context/BranchContext';
import { ClinicProvider, useClinic } from './context/ClinicContext';
import { setupAxiosVeeranshInterceptor, subscribeVeeranshError } from './utils/veeranshErrorBus';
import { setupActionGuardInterceptor } from './utils/actionGuardBus';
import ActionGuardPrompt from './components/ActionGuardPrompt';
import ActionGuardPage from './pages/ActionGuardPage';
import { initSyncLock, onSyncMismatch, onSyncChange, resetStoredInstallId } from './utils/syncLock';
import DoctorMasterPage from './pages/DoctorMasterPage';
import FaceCheckin from './components/FaceCheckin';
import { registerFace } from './utils/faceUtils';
import BranchMasterPage from './pages/BranchMasterPage';
import RecallPage from './pages/RecallPage';
import CallingModePage from './pages/CallingModePage';
import TelephonyHubPage from './pages/TelephonyHubPage';
import InventoryPage from './pages/InventoryPage';
const LabOrdersPage = lazy(() => import('./pages/LabOrdersPage'));
import ExpensesPage from './pages/ExpensesPage';
import PackagesPage from './pages/PackagesPage';
import InsurancePage from './pages/InsurancePage';
import OnlineBookingsPage from './pages/OnlineBookingsPage';
import MarketingPage from './pages/MarketingPage';
import AuditLogPage from './pages/AuditLogPage';
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));
// 11 WhatsApp pages consolidated into WhatsAppHubPage with internal tabs.
import WhatsAppHubPage from './pages/WhatsAppHubPage';
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
import SecurityPage from './pages/SecurityPage';
import BillingPage from './pages/BillingPage';
import FinancialControlsPage from './pages/FinancialControlsPage';
// PharmacyAdvancedPage + PharmacySmartPage moved under PharmacyPage as sub-tabs
// (the bodies now live at client/src/components/PharmacyAdvancedPanel.jsx and
// PharmacySmartPanel.jsx).
import TaxCompliancePage from './pages/TaxCompliancePage';
import DayEndPage from './pages/DayEndPage';
import AnalyticsHubPage from './pages/AnalyticsHubPage';
import IntegrationsHubPage from './pages/IntegrationsHubPage';
import CallLogPage from './pages/CallLogPage';
import TrackedNumbersPage from './pages/TrackedNumbersPage';
import FrontDeskInboxPage from './pages/FrontDeskInboxPage';
import RecareCampaignsPage from './pages/RecareCampaignsPage';
import TeleMedPage from './pages/TeleMedPage';
import HealthScorePage from './pages/HealthScorePage';
import FaxPage from './pages/FaxPage';
import VoipPage from './pages/VoipPage';
import LiveCallPopup from './components/LiveCallPopup';
import IncomingCallPopup from './components/IncomingCallPopup';
import DeviceGate from './components/security/DeviceGate';
import SOPLibraryPage from './pages/SOPLibraryPage';
const SOPEditorPage = lazy(() => import('./pages/SOPEditorPage'));
import EditableDocsPage from './pages/EditableDocsPage';
import StaffDirectoryPage from './pages/StaffDirectoryPage';
const StaffAttendancePage = lazy(() => import('./pages/StaffAttendancePage'));
const HRMasterPage = lazy(() => import('./pages/HRMasterPage'));
// Phase 10-14 pages
import PatientPortalPage from './pages/PatientPortalPage';
import PublicBookingPage from './pages/PublicBookingPage';
import LabPortalPage from './pages/LabPortalPage';
const LabAppPage = lazy(() => import('./pages/LabAppPage'));
import PatientReferralsPage from './pages/PatientReferralsPage';
import DoctorCommissionsPage from './pages/DoctorCommissionsPage';
import FranchisePage from './pages/FranchisePage';
import ClinicalQualityPage from './pages/ClinicalQualityPage';
import SmartSchedulePage from './pages/SmartSchedulePage';
// Phase 17 + clinical + business pages
import RiskScoresPage from './pages/RiskScoresPage';
import DentalChartPage from './pages/DentalChartPage';
import RadiographsPage from './pages/RadiographsPage';
import GiftCardsPage from './pages/GiftCardsPage';
const CouponManagerPage = lazy(() => import('./pages/CouponManagerPage'));
import InventoryForecastPage from './pages/InventoryForecastPage';
// Phase I — Surface hidden backend
import WaitListPage from './pages/WaitListPage';
import SupplierScorecardPage from './pages/SupplierScorecardPage';
import PatientLTVPage from './pages/PatientLTVPage';
import TreatmentOutcomesPage from './pages/TreatmentOutcomesPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import ClinicalProtocolsPage from './pages/ClinicalProtocolsPage';
import VitalSignsPage from './pages/VitalSignsPage';
import FraudDetectionPage from './pages/FraudDetectionPage';
import DynamicPricingPage from './pages/DynamicPricingPage';
import LiteratureSearchPage from './pages/LiteratureSearchPage';
import ScannerImportPage from './pages/ScannerImportPage';
import StaffCheckInPage from './pages/StaffCheckInPage';
import CertificatesPage from './pages/CertificatesPage';
import BackupsPage from './pages/BackupsPage';
import KPIRegisterPage from './pages/KPIRegisterPage';
const NABHPage = lazy(() => import('./pages/NABHPage'));
import ComplianceLogsPage from './pages/ComplianceLogsPage';
import EMIPlansPage from './pages/EMIPlansPage';
import ProfitabilityReportPage from './pages/ProfitabilityReportPage';
import GovSchemesPage from './pages/GovSchemesPage';
// NABH sign-off + scorecard are now sub-tabs of NABHPage — their standalone
// pages were consolidated into components/NABHSignOffPanel.jsx and
// components/NABHScorecardPanel.jsx.
import CommitteeMeetingsPage from './pages/CommitteeMeetingsPage';
import MockAuditPage from './pages/MockAuditPage';
import PatientSafetyPage from './pages/PatientSafetyPage';
import DispensingRegisterPage from './pages/DispensingRegisterPage';
const TreatmentCompletionPage = lazy(() => import('./pages/TreatmentCompletionPage'));
import InspectorPackagePage from './pages/InspectorPackagePage';
import RetentionPage from './pages/RetentionPage';
import AlertsPage from './pages/AlertsPage';
import ReviewsPage from './pages/ReviewsPage';
import HuddlePage from './pages/HuddlePage';
import FormBuilderPage from './pages/FormBuilderPage';
import CheckinQueuePage from './pages/CheckinQueuePage';
import ChatPage from './pages/ChatPage';
import InsuranceVerifyPage from './pages/InsuranceVerifyPage';
import DataMigrationPage from './pages/DataMigrationPage';
import AIInsightsPage from './pages/AIInsightsPage';
const VeeranshTrainingPage = lazy(() => import('./pages/VeeranshTrainingPage'));
import IndiaCompliancePage from './pages/IndiaCompliancePage';
const PremiumPage = lazy(() => import('./pages/PremiumPage'));
import ClinicalPage from './pages/ClinicalPage';
const RegistersPage = lazy(() => import('./pages/RegistersPage'));
import AuditPrepPage from './pages/AuditPrepPage';
import StaffTasksPage from './pages/StaffTasksPage';
import StaffOnboardingPage from './pages/StaffOnboardingPage';
import VeeranshAI from './components/VeeranshAI';

// Heavy infrequent pages — lazy-loaded to keep the initial JS bundle small.
// Without this the first paint blocks on ~14K LOC of Settings/Reports/HR/etc
// even though the user is just viewing the dashboard. Wrapped in a Suspense
// boundary below; the active tab still feels instant because each page
// finishes its dynamic import in parallel with the API fetches that render
// requires anyway.
const ReportsPage          = lazy(() => import('./pages/ReportsPage'));
const SettingsPage         = lazy(() => import('./pages/SettingsPage'));
const ExpenseAnalyzerPage  = lazy(() => import('./pages/ExpenseAnalyzerPage'));
const PharmacyPage         = lazy(() => import('./pages/PharmacyPage'));

const PAGE_LABELS = (() => {
  const m = Object.fromEntries(NAV_ITEMS.map(n => [n.key, n.label]));
  [...CLINICAL_CARDS, ...BILLING_CARDS, ...PHARMACY_CARDS, ...STAFF_CARDS, ...MARKETING_CARDS]
    .forEach(c => { m[c.key] = c.label; });
  return m;
})();

// ── Global axios auth interceptor ──
// Attaches the stored JWT to every /api/* request and silently refreshes
// the access token when it expires (which used to surface as "all data
// disappeared mid-session" because every list call was 401-ing into an
// empty array catch).
if (!axios.__oralzyAuthAttached) {
  axios.interceptors.request.use((config) => {
    if ((config.url || '').includes('/api/')) {
      const token = localStorage.getItem('dentalToken');
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  // Response interceptor — on 401 try to refresh once, replay the request,
  // and on refresh failure clear local auth + redirect to login. Without
  // this, every page that fetches lists just shows blank silently after
  // the access TTL (1h) elapses.
  let refreshInFlight = null;

  const doRefresh = () => {
    if (refreshInFlight) return refreshInFlight;
    const refreshToken = localStorage.getItem('dentalRefreshToken');
    if (!refreshToken) return Promise.reject(new Error('no-refresh-token'));
    // Build the refresh URL the same way the rest of the app picks API_BASE.
    const apiBaseEnv = process.env.REACT_APP_API_BASE || '';
    const isProd = window.location.protocol === 'https:' || process.env.NODE_ENV === 'production';
    const ip = window.location.hostname || 'localhost';
    const apiBase = apiBaseEnv || (isProd ? '/api' : `http://${ip}:5000/api`);
    refreshInFlight = axios.post(`${apiBase}/saas-auth/refresh`, { refreshToken }, { _skipAuthRetry: true })
      .then(res => {
        if (res.data.accessToken)  localStorage.setItem('dentalToken', res.data.accessToken);
        if (res.data.refreshToken) localStorage.setItem('dentalRefreshToken', res.data.refreshToken);
        return res.data.accessToken;
      })
      .finally(() => { refreshInFlight = null; });
    return refreshInFlight;
  };

  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error.config || {};
      const status = error.response?.status;
      // Only attempt refresh once per request, only on 401, and only for
      // /api/* calls that ride on the same auth scheme.
      if (status === 401 && cfg && !cfg._skipAuthRetry && (cfg.url || '').includes('/api/') && !(cfg.url || '').includes('/saas-auth/refresh') && !(cfg.url || '').includes('/saas-auth/login')) {
        cfg._skipAuthRetry = true;
        try {
          const newToken = await doRefresh();
          if (newToken) {
            cfg.headers = cfg.headers || {};
            cfg.headers.Authorization = `Bearer ${newToken}`;
            return axios(cfg);
          }
        } catch (e) {
          // Refresh failed — clear stale credentials and bounce to login so the
          // user understands what happened instead of staring at blank lists.
          localStorage.removeItem('dentalToken');
          localStorage.removeItem('dentalRefreshToken');
          localStorage.removeItem('dentalUser');
          // 'no-refresh-token' means we were never logged in (or already
          // cleared) — reloading here just produces an infinite loop because
          // the next render fires the same 401 again. Let the caller's
          // catch handle it; the app will render LoginPage on its own.
          if (e?.message === 'no-refresh-token') return Promise.reject(error);
          if (!window.location.pathname.includes('login')) {
            console.warn('[auth] refresh failed — redirecting to login');
            window.location.reload();
          }
        }
      }
      return Promise.reject(error);
    }
  );

  axios.__oralzyAuthAttached = true;
}

// Single source of truth for the backend host
const IP_ADDRESS = window.location.hostname || 'localhost';
const IS_PRODUCTION = window.location.protocol === 'https:' || process.env.NODE_ENV === 'production';
// In production (Cloudflare Pages) the React app is hosted separately from
// the Node API — so /api relative paths won't work. Use REACT_APP_API_BASE
// baked in at build time to point at the Render backend.
const API_BASE_ENV = process.env.REACT_APP_API_BASE || '';

// Design system, navigation, and shared constants are imported from
// ./theme, ./constants, and ./layout/Sidebar (NAV_ITEMS).

// Tiny placeholder shown while a lazily-loaded page chunk is downloading.
// Avoids a blank flash and gives the user a "something is happening" cue.
const PageLoading = () => (
  <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-secondary, #888)' }}>
    <div style={{ fontSize: 13, fontWeight: 600 }}>Loading…</div>
  </div>
);

function AppInner() {
  const { branches, doctors, activeBranch, setActiveBranch, refreshBranches, refreshDoctors } = useBranch();
  const { clinic } = useClinic();
  // --- CONFIGURATION ---
  const IP = IP_ADDRESS;
  // Priority: 1) env var (prod split deploy), 2) /api (same-origin), 3) local IP
  const API_BASE = API_BASE_ENV || (IS_PRODUCTION ? '/api' : `http://${IP}:5000/api`);
  const UPLOAD_BASE = API_BASE_ENV
    ? API_BASE_ENV.replace(/\/api$/, '/uploads')
    : (IS_PRODUCTION ? '/uploads' : `http://${IP}:5000/uploads`);

  // --- STATES ---
  const [pharmacyMode, setPharmacyMode] = useState(false);
  const [pharmacyEnabled, setPharmacyEnabled] = useState(false);
  // Registration/OPD fee clinic defaults — read on app load so the
  // registration form can pre-fill amount + validity preview.
  const [regFeeConfig, setRegFeeConfig] = useState({
    enabled: false, amount: 0, validityDays: 15, label: 'Registration / OPD Fee',
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  // Dev-only hook: expose setter for E2E browser tests so test harness can
  // navigate without simulating clicks. Guarded by NODE_ENV so it's tree-
  // shaken out of production bundles.
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    window.__setActiveTab = setActiveTab;
  }
  const [showVeeransh, setShowVeeransh] = useState(false);
  // When an error bubbles up through the interceptor we auto-open Veeransh
  // with the error context so it can explain and guide. Cleared on close.
  const [veeranshErrorContext, setVeeranshErrorContext] = useState(null);

  // Database lock — fires when this browser is suddenly hitting a different
  // backend than before (someone changed API URL / pointed at wrong server).
  const [syncMismatch, setSyncMismatch] = useState(null);

  // Wire the axios interceptor + error subscription — once per app lifetime.
  useEffect(() => {
    setupAxiosVeeranshInterceptor(axios);
    setupActionGuardInterceptor(API_BASE);
    const unsub = subscribeVeeranshError((err) => {
      setVeeranshErrorContext({ ...err, page: activeTab });
      setShowVeeransh(true);
    });

    // Start the sync lock — verifies we're talking to the same backend
    // every 30 s. If it ever changes, syncMismatch fires and the UI locks.
    initSyncLock(API_BASE).catch(() => {});
    const unsubMis = onSyncMismatch((info) => setSyncMismatch(info));
    const unsubChg = onSyncChange(() => {
      // A write happened somewhere (web or mobile) — refresh the active
      // tab's data so the dentist sees it without manually reloading.
      try {
        if (activeTab === 'dashboard')    fetchDashboardStats();
        if (activeTab === 'list')         fetchAllPatients();
        if (activeTab === 'appointments') fetchAppointments();
      } catch {}
    });

    return () => { unsub(); unsubMis(); unsubChg(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [regStep, setRegStep] = useState(1);
  const [calView, setCalView] = useState(() => localStorage.getItem('calDefaultView') || 'week');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [weekStartDay, setWeekStartDay] = useState(() => Number(localStorage.getItem('weekStartDay') || 0));
  const [calWeekStart, setCalWeekStart] = useState(() => { const pos = Number(localStorage.getItem('calTodayPosition') || 2); const d = new Date(); d.setDate(d.getDate() - pos); return d.toISOString().split('T')[0]; });
  const [calDayDate, setCalDayDate] = useState(new Date().toISOString().split('T')[0]);
  const [selWeekDay, setSelWeekDay] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotClick, setSlotClick] = useState(null);
  const [selAppt, setSelAppt] = useState(null);
  const [calBookForm, setCalBookForm] = useState({ patientName: '', phone: '', time: '', treatment: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [patientsList, setPatientsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({ totalPatients: 0, todaysPatients: 0, totalRevenue: 0, todaysRevenue: 0 });
  const [medicalList, setMedicalList] = useState([]);
  const [dentalList, setDentalList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [medicinesList, setMedicinesList] = useState([]);
  const [allergiesList, setAllergiesList] = useState([]);
  const [newSettingItem, setNewSettingItem] = useState({ category: 'group', label: '' });
  const [newSetting, setNewSetting] = useState({ category: 'medical', label: '' });

  // Patient Registration State
  const [patient, setPatient] = useState(INITIAL_PATIENT);

  // Prescription States
  const [isWritingPrescription, setIsWritingPrescription] = useState(false);
  const [pastPrescriptions, setPastPrescriptions] = useState([]);
  const [prescriptionForm, setPrescriptionForm] = useState({ chiefComplaint: '', treatments: [], medicines: [], notes: '' });
  const [currentMed, setCurrentMed] = useState({ name: '', dosage: '', duration: '', instructions: '' });
  const [treatmentInput, setTreatmentInput] = useState('');
  const [printData, setPrintData] = useState(null);

  // Billing States
  const [isWritingBill, setIsWritingBill] = useState(false);
  const [pastBills, setPastBills] = useState([]);
  const [billForm, setBillForm] = useState({ items: [], discount: 0 });
  const [currentBillItem, setCurrentBillItem] = useState({ description: '', amount: '' });
  const [printBillData, setPrintBillData] = useState(null);

  // Patient profile tab
  const [patientTab, setPatientTab] = useState(''); // '' = visit timeline | 'payments' | 'documents' | 'chart' | 'notes' | 'feedback'
  const [pastTreatments, setPastTreatments] = useState([]);

  // Treatment Types (settings se)
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [newTreatmentType, setNewTreatmentType] = useState({ name:'', defaultPrice:0, defaultUnits:1, gstPercent:0, consentTemplate:'', linkedInstructions:[], linkedMedicines:[], labWorkRequired:false, labLeadDays:7 });
  const [editingTT, setEditingTT] = useState(null);
  const [instructionList, setInstructionList] = useState([]);
  // Medicine Library (shared across all TT types)
  const [medicineLibrary, setMedicineLibrary] = useState([]);
  const [newMedLib, setNewMedLib] = useState({ name:'', dosage:'', duration:'', instructions:'' });

  // Prescription preset lists (from Settings)
  const [chiefComplaintList, setChiefComplaintList] = useState([]);
  const [findingsList, setFindingsList] = useState([]);
  const [investigationList, setInvestigationList] = useState([]);
  const [diagnosisList, setDiagnosisList] = useState([]);

  // Appointment States
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [appointmentForm, setAppointmentForm] = useState({ patientName: '', phone: '', date: '', time: '', treatment: '' });

  // Report/X-Ray States
  const [patientReports, setPatientReports] = useState([]);
  const [reportFile, setReportFile] = useState(null);
  const [reportTitle, setReportTitle] = useState('');

  // Settings accordion
  const [openSettingSection, setOpenSettingSection] = useState(null);

  // Sidebar collapse — auto-collapse on narrow viewports (tablet/mobile)
  // so the locked 11-item sidebar doesn't steal precious horizontal space.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );
  // Mobile drawer toggle. The sidebar is `display: none` < 768px (see
  // index.css), so the only way for phone users to reach navigation is
  // via the hamburger in Header → opens the sidebar as a fixed drawer.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 900) setSidebarCollapsed(true);
      // Close the drawer on resize back up to desktop.
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Face checkin
  const [showFaceCheckin, setShowFaceCheckin] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K → Global Search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setShowGlobalSearch(true); }
      // Cmd+N or Ctrl+N → New Patient
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') { e.preventDefault(); setActiveTab('register'); setRegStep(1); }
      // Ctrl+P or Cmd+P → Print (if patient selected)
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        if (selectedPatient) { e.preventDefault(); window.print(); }
      }
      // Ctrl+F or Cmd+F → Focus search (same as Cmd+K)
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') { e.preventDefault(); setShowGlobalSearch(true); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPatient]); // eslint-disable-line
  const [showRegForm, setShowRegForm] = useState(null); // patient object to show form for

  // Analytics / Business Reports
  const [reportCategory, setReportCategory] = useState(null); // 'summary' | 'patient' | 'appointments' | 'treatments' | 'accounting' | 'lab'
  const [reportSubReport, setReportSubReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoadingSub, setReportLoadingSub] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState('summary');
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [patientGrowth, setPatientGrowth] = useState([]);
  const [treatmentStats, setTreatmentStats] = useState([]);
  const [appointmentStats, setAppointmentStats] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [inactivePatients, setInactivePatients] = useState(null);
  const [topPatients, setTopPatients] = useState([]);
  const [referralStats, setReferralStats] = useState([]);
  const [prescriptionSummary, setPrescriptionSummary] = useState(null);
  const [monthComparison, setMonthComparison] = useState(null);
  const [billAnalytics, setBillAnalytics] = useState(null);
  const [topRevenueDays, setTopRevenueDays] = useState([]);
  const [revenueByTreatment, setRevenueByTreatment] = useState([]);
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState(() => { const d=new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [analyticsDateTo, setAnalyticsDateTo]     = useState(new Date().toISOString().split('T')[0]);

  // WhatsApp section (dashboard)
  const [waSearch, setWaSearch] = useState('');
  const [waSelectedPt, setWaSelectedPt] = useState(null);
  const [waCustomMsg, setWaCustomMsg] = useState('');

  const [uhidConfig, setUhidConfig] = useState({ lastNumber: 0, prefix: 'SDH', isRandom: false });

  // Rate Plan States
  const [ratePlans, setRatePlans] = useState([]);
  const [newPlanName, setNewPlanName] = useState('');
  const [newTreatment, setNewTreatment] = useState({ name: '', amount: '' });
  const [editingTreatment, setEditingTreatment] = useState(null);

  // Auth States
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dentalUser')) || null; } catch { return null; }
  });
  // Landing-page gate: users land on the static landing site (index.html) first.
  // Clicking Login/Signup there dispatches `oralzy-start` which flips this flag.
  const [showAuth, setShowAuth] = useState(() => sessionStorage.getItem('oralzySkipLanding') === '1');
  const [authView, setAuthView] = useState(
    () => sessionStorage.getItem('oralzyAuthView') === 'clinic-signup' ? 'clinic-signup' : 'login'
  );
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [authForm, setAuthForm] = useState({ username: '', password: '', fullName: '' });
  const [authMsg, setAuthMsg] = useState({ type: '', text: '' });
  const [authLoading, setAuthLoading] = useState(false);

  // Session timeout — auto-logout after inactivity
  const [lastActivity, setLastActivity] = React.useState(Date.now());
  const sessionTimeout = Number(localStorage.getItem('sessionTimeout') || '30'); // minutes

  // Bridge: listen for CTA clicks from the static landing page in public/index.html.
  React.useEffect(() => {
    const onStart = (e) => {
      if (e?.detail?.mode === 'signup') setAuthView('clinic-signup');
      else setAuthView('login');
      setShowAuth(true);
    };
    const onBack = () => {
      // User hit browser-back from /login or /signup → return to marketing site.
      setShowAuth(false);
      setAuthView('login');
    };
    window.addEventListener('oralzy-start', onStart);
    window.addEventListener('oralzy-back-to-landing', onBack);
    return () => {
      window.removeEventListener('oralzy-start', onStart);
      window.removeEventListener('oralzy-back-to-landing', onBack);
    };
  }, []);

  // Sync `app-active` class on <body> so the static landing hides when React owns the screen.
  React.useEffect(() => {
    const appActive = Boolean(currentUser) || showAuth || authView === 'clinic-signup';
    document.body.classList.toggle('app-active', appActive);
  }, [currentUser, showAuth, authView]);

  React.useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const resetTimer = () => setLastActivity(Date.now());
    events.forEach(e => window.addEventListener(e, resetTimer));

    const checker = setInterval(() => {
      if (currentUser && (Date.now() - lastActivity) > sessionTimeout * 60 * 1000) {
        alert('Session expired due to inactivity. Please login again.');
        handleLogout();
      }
    }, 60000); // check every minute

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearInterval(checker);
    };
  }, [currentUser, lastActivity, sessionTimeout]);

  // User Management States (admin)
  const [usersList, setUsersList] = useState([]);
  const [newUserForm, setNewUserForm] = useState({ username: '', password: '', fullName: '', role: 'staff' });
  const [changePwForm, setChangePwForm] = useState({ oldPassword: '', newPassword: '' });

  // Webcam States
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoBlob, setPhotoBlob] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // --- DATA FETCHING ---
  useEffect(() => {
    fetchMasterLists(); fetchUhidSettings(); fetchAllPatients(); fetchDashboardStats(); fetchAppointments(); fetchRatePlans(); fetchTreatmentTypes();
    // Check if pharmacy module is enabled + load registration-fee defaults
    axios.get(`${API_BASE}/admin/clinic-settings`).then(r => {
      setPharmacyEnabled(!!r.data.pharmacyEnabled);
      setRegFeeConfig({
        enabled:      !!r.data.registrationFeeEnabled,
        amount:       Number(r.data.registrationFee) || 0,
        validityDays: Number(r.data.registrationFeeValidityDays) || 15,
        label:        r.data.registrationFeeLabel || 'Registration / OPD Fee',
      });
    }).catch(() => {});
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  // Fetch session timeout setting
  React.useEffect(() => {
    if (currentUser) {
      axios.get(`${API_BASE}/admin/clinic-settings`).then(r => {
        if (r.data?.sessionTimeoutMinutes) localStorage.setItem('sessionTimeout', r.data.sessionTimeoutMinutes);
      }).catch(() => {});
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboardStats();
    if (activeTab === 'appointments') fetchAppointments();
    if (activeTab === 'list') fetchAllPatients();
    if (activeTab === 'analytics') loadAllAnalytics(analyticsDateFrom, analyticsDateTo);
  }, [activeTab]);

  useEffect(() => {
    if (selectedPatient) {
      fetchPastPrescriptions(selectedPatient._id);
      fetchPastBills(selectedPatient._id);
      fetchPatientReports(selectedPatient._id);
      axios.get(`${API_BASE}/treatments/patient/${selectedPatient._id}`).then(r=>setPastTreatments(r.data)).catch(()=>{});
    }
  }, [selectedPatient]);

  const fetchDashboardStats = async () => { try { const res = await axios.get(`${API_BASE}/dashboard/stats`); setDashboardStats(res.data); } catch (e) {} };
  const fetchAppointments = async () => { try { const res = await axios.get(`${API_BASE}/appointments`); setAppointmentsList(res.data); } catch (e) {} };
  const fetchAllPatients = async () => { try { const res = await axios.get(`${API_BASE}/patients?lite=1`); setPatientsList(res.data); } catch (e) {} };
  const fetchMasterLists = async () => {
    try {
      const [m, d, g, t, med, al, cc, fn, inv, diag, instr, medLib] = await Promise.all([
        axios.get(`${API_BASE}/settings/master-list/medical`),
        axios.get(`${API_BASE}/settings/master-list/dental`),
        axios.get(`${API_BASE}/settings/master-list/group`),
        axios.get(`${API_BASE}/settings/master-list/tag`),
        axios.get(`${API_BASE}/settings/master-list/medicine`),
        axios.get(`${API_BASE}/settings/master-list/allergy`),
        axios.get(`${API_BASE}/settings/master-list/chiefComplaint`),
        axios.get(`${API_BASE}/settings/master-list/findings`),
        axios.get(`${API_BASE}/settings/master-list/investigation`),
        axios.get(`${API_BASE}/settings/master-list/diagnosis`),
        axios.get(`${API_BASE}/settings/master-list/instruction`),
        axios.get(`${API_BASE}/settings/master-list/medLibrary`),
      ]);
      setMedicalList(m.data); setDentalList(d.data);
      setGroupsList(g.data); setTagsList(t.data);
      setMedicinesList(med.data); setAllergiesList(al.data);
      setChiefComplaintList(cc.data); setFindingsList(fn.data);
      setInvestigationList(inv.data); setDiagnosisList(diag.data);
      setInstructionList(instr.data);
      setMedicineLibrary(medLib.data);
    } catch (e) {}
  };
  const fetchTreatmentTypes = async () => {
    try { const res = await axios.get(`${API_BASE}/treatment-types`); setTreatmentTypes(res.data); } catch (e) {}
  };
  const fetchUhidSettings = async () => { try { const res = await axios.get(`${API_BASE}/settings/uhid-settings`); if (res.data) setUhidConfig(res.data); } catch (e) {} };
  const fetchRatePlans = async () => { try { const res = await axios.get(`${API_BASE}/settings/rate-plans`); setRatePlans(res.data); } catch (e) {} };
  const fetchPastPrescriptions = async (id) => { try { const res = await axios.get(`${API_BASE}/prescriptions/patient/${id}`); setPastPrescriptions(res.data); } catch (e) {} };
  const fetchPastBills = async (id) => { try { const res = await axios.get(`${API_BASE}/bills/patient/${id}`); setPastBills(res.data); } catch (e) {} };
  const fetchPatientReports = async (id) => { try { const res = await axios.get(`${API_BASE}/reports/patient/${id}`); setPatientReports(res.data); } catch (e) {} };

  // Analytics fetch helpers
  const fetchAnalyticsSummary    = async () => { try { const r=await axios.get(`${API_BASE}/analytics/summary`); setAnalyticsSummary(r.data); } catch(e){} };
  const fetchDailyRevenue        = async (from,to) => { try { const r=await axios.get(`${API_BASE}/analytics/daily-revenue?from=${from}&to=${to}`); setDailyRevenue(r.data); } catch(e){} };
  const fetchMonthlyRevenue      = async () => { try { const r=await axios.get(`${API_BASE}/analytics/monthly-revenue`); setMonthlyRevenue(r.data); } catch(e){} };
  const fetchPatientGrowth       = async () => { try { const r=await axios.get(`${API_BASE}/analytics/patient-growth`); setPatientGrowth(r.data); } catch(e){} };
  const fetchTreatmentStats      = async () => { try { const r=await axios.get(`${API_BASE}/analytics/treatment-stats`); setTreatmentStats(r.data); } catch(e){} };
  const fetchAppointmentStats    = async () => { try { const r=await axios.get(`${API_BASE}/analytics/appointment-stats`); setAppointmentStats(r.data); } catch(e){} };
  const fetchDemographics        = async () => { try { const r=await axios.get(`${API_BASE}/analytics/demographics`); setDemographics(r.data); } catch(e){} };
  const fetchInactivePatients    = async (days) => { try { const r=await axios.get(`${API_BASE}/analytics/inactive?days=${days||90}`); setInactivePatients(r.data); } catch(e){} };
  const fetchTopPatients         = async () => { try { const r=await axios.get(`${API_BASE}/analytics/top-patients?limit=10`); setTopPatients(r.data); } catch(e){} };
  const fetchReferralStats       = async () => { try { const r=await axios.get(`${API_BASE}/analytics/referral-stats`); setReferralStats(r.data); } catch(e){} };
  const fetchPrescriptionSummary = async () => { try { const r=await axios.get(`${API_BASE}/analytics/prescription-summary`); setPrescriptionSummary(r.data); } catch(e){} };
  const fetchMonthComparison     = async () => { try { const r=await axios.get(`${API_BASE}/analytics/month-comparison`); setMonthComparison(r.data); } catch(e){} };
  const fetchBillAnalytics       = async () => { try { const r=await axios.get(`${API_BASE}/analytics/bill-analytics`); setBillAnalytics(r.data); } catch(e){} };
  const fetchTopRevenueDays      = async () => { try { const r=await axios.get(`${API_BASE}/analytics/top-revenue-days`); setTopRevenueDays(r.data); } catch(e){} };
  const fetchRevenueByTreatment  = async (from,to) => { try { const r=await axios.get(`${API_BASE}/analytics/revenue-by-treatment?from=${from}&to=${to}`); setRevenueByTreatment(r.data); } catch(e){} };

  const fetchSubReport = async (key, params={}) => {
    setReportLoadingSub(true);
    setReportData(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const url = `${API_BASE}/analytics/${key}${qs?'?'+qs:''}`;
      const r = await axios.get(url);
      setReportData(r.data);
    } catch(e) {
      setReportData({ error: e.response?.data?.error || e.message });
    }
    setReportLoadingSub(false);
  };

  const loadAllAnalytics = async (from, to) => {
    setAnalyticsLoading(true);
    await Promise.all([
      fetchAnalyticsSummary(), fetchDailyRevenue(from,to), fetchMonthlyRevenue(),
      fetchPatientGrowth(), fetchTreatmentStats(), fetchAppointmentStats(),
      fetchDemographics(), fetchTopPatients(), fetchReferralStats(),
      fetchPrescriptionSummary(), fetchMonthComparison(), fetchBillAnalytics(),
      fetchTopRevenueDays(), fetchRevenueByTreatment(from,to),
    ]);
    setAnalyticsLoading(false);
  };

  // --- WEBCAM HANDLERS ---
  const startWebcam = async () => {
    setShowWebcam(true);
    setCapturedPhoto(null);
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    streamRef.current = stream;
    setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
  };

  const capturePhoto = () => {
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => setPhotoBlob(blob), 'image/jpeg');
    setCapturedPhoto(canvas.toDataURL('image/jpeg'));
    stopWebcam();
  };

  const stopWebcam = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setShowWebcam(false);
  };

  const retakePhoto = () => { setCapturedPhoto(null); setPhotoBlob(null); startWebcam(); };

  // --- HANDLERS ---
  const handleSavePatient = async () => {
    try {
      // AI Duplicate Detector — check before creating (skip if user already forced)
      if (!patient.forceAdd) {
        try {
          const dupRes = await axios.post(`${API_BASE}/patients/duplicate-check`, {
            name: patient.name, phone: patient.phone, dob: patient.dob,
          });
          if (dupRes.data?.hasDuplicates && dupRes.data.matches?.length) {
            const lines = dupRes.data.matches.slice(0, 3).map(m =>
              `• ${m.name} (UHID ${m.uhid}, ${m.phone}) — ${m.confidence.toUpperCase()} [${m.reasons.join(', ')}]`
            ).join('\n');
            const proceed = window.confirm(
              `⚠️ Possible duplicate patient(s) detected:\n\n${lines}\n\nClick OK to continue anyway, Cancel to review.`
            );
            if (!proceed) return;
          }
        } catch (e) { /* fall through — do not block registration if check fails */ }
      }

      const formData = new FormData();
      Object.keys(patient).forEach(key => {
        if (key === 'insurance') formData.append(key, JSON.stringify(patient[key]));
        else if (key === 'regFee') {
          // Only send when clinic has the fee enabled; backend JSON-parses.
          if (regFeeConfig?.enabled) formData.append('regFee', JSON.stringify(patient.regFee || {}));
        }
        else formData.append(key, patient[key]);
      });
      if (photoBlob) formData.append('photo', photoBlob, 'patient_photo.jpg');
      const res = await axios.post(`${API_BASE}/patients/add`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.warning) alert(res.data.message);
      else {
        // Auto-register face if photo was captured
        let faceMsg = '';
        if (capturedPhoto && res.data._id) {
          const faceOk = await registerFace(API_BASE, res.data._id, capturedPhoto);
          faceMsg = faceOk ? '\nFace registered for check-in.' : '\nFace could not be detected — register manually from profile.';
        }
        // Show printable registration form
        const savedPatient = { ...patient, uhid: res.data.uhid, _id: res.data._id, createdAt: new Date() };
        setShowRegForm(savedPatient);

        fetchAllPatients(); fetchDashboardStats();
        setPatient(INITIAL_PATIENT);
        setCapturedPhoto(null); setPhotoBlob(null);
      }
    } catch (err) { alert('Error saving patient'); }
  };

  const handleFileUpload = async () => {
    if (!reportFile || !reportTitle) return alert("Title aur Photo dono zaruri hain!");
    const formData = new FormData();
    formData.append('image', reportFile);
    formData.append('patientId', selectedPatient._id);
    formData.append('title', reportTitle);
    try {
      await axios.post(`${API_BASE}/reports/upload`, formData);
      alert("X-Ray Uploaded!");
      setReportFile(null); setReportTitle('');
      fetchPatientReports(selectedPatient._id);
    } catch (err) { alert("Upload fail!"); }
  };

  const handleAddMedicine = () => { if (!currentMed.name) return; setPrescriptionForm(prev => ({ ...prev, medicines: [...prev.medicines, currentMed] })); setCurrentMed({ name: '', dosage: '', duration: '', instructions: '' }); };
  const handleSavePrescription = async () => {
    try {
      await axios.post(`${API_BASE}/prescriptions/add`, { ...prescriptionForm, patientId: selectedPatient._id });
      alert("Parcha Saved!");
      setIsWritingPrescription(false);
      setPrescriptionForm({ chiefComplaint: '', treatments: [], medicines: [], notes: '' });
      fetchPastPrescriptions(selectedPatient._id);
    } catch (err) {}
  };

  const handleAddBillItem = () => { if (!currentBillItem.description || !currentBillItem.amount) return; setBillForm(prev => ({ ...prev, items: [...prev.items, currentBillItem] })); setCurrentBillItem({ description: '', amount: '' }); };
  const billTotal = billForm.items.reduce((s, i) => s + Number(i.amount), 0) - Number(billForm.discount || 0);
  const handleSaveBill = async () => {
    try {
      await axios.post(`${API_BASE}/bills/add`, { patientId: selectedPatient._id, items: billForm.items, discount: billForm.discount, totalAmount: billTotal });
      alert("Bill Saved!");
      setIsWritingBill(false);
      setBillForm({ items: [], discount: 0 });
      fetchPastBills(selectedPatient._id);
      fetchDashboardStats();
    } catch (err) {}
  };

  const handleCalBook = async () => {
    if (!calBookForm.patientName || !calBookForm.time) return alert('Naam aur Time zaruri hain!');
    if (!slotClick) return;
    try {
      await axios.post(`${API_BASE}/appointments/add`, { ...calBookForm, date: slotClick.date });
      fetchAppointments();
      alert('Appointment Book Ho Gayi!');
      // Auto WhatsApp
      if (calBookForm.phone) whatsappAppointmentBooked(calBookForm.patientName, calBookForm.phone, slotClick.date, calBookForm.time, calBookForm.treatment);
      setCalBookForm({ patientName: '', phone: '', time: '', treatment: '' });
      setSlotClick(null);
    } catch (e) { alert('Error!'); }
  };

  const handleBookAppointment = async () => {
    if (!appointmentForm.patientName || !appointmentForm.date || !appointmentForm.time) {
      return alert("Naam, Date aur Time zaruri hain!");
    }
    try {
      await axios.post(`${API_BASE}/appointments/add`, appointmentForm);
      fetchAppointments();
      alert('Appointment Book Ho Gayi!');
      // Auto WhatsApp
      if (appointmentForm.phone) whatsappAppointmentBooked(appointmentForm.patientName, appointmentForm.phone, appointmentForm.date, appointmentForm.time, appointmentForm.treatment);
      setAppointmentForm({ patientName: '', phone: '', date: '', time: '', treatment: '' });
    } catch (e) {
      alert('Error: Appointment book nahi hui');
    }
  };

  const handleAddSettingItem = async () => {
    if (!newSettingItem.label.trim()) return alert('Naam likho!');
    try { await axios.post(`${API_BASE}/settings/master-list`, newSettingItem); setNewSettingItem({ ...newSettingItem, label: '' }); fetchMasterLists(); }
    catch (e) { alert('Ye naam pehle se hai!'); }
  };

  // ── Patient Card Print ──
  const printPatientCard = (p) => {
    if (!p) return;
    const w = window.open('', '_blank', 'width=500,height=400');
    const sinceDate = p.patientSince ? new Date(p.patientSince).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : 'N/A';
    const dob = p.dob ? new Date(p.dob).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : '';
    const photoSrc = p.photo ? (/^https?:\/\//.test(p.photo) ? p.photo : `${UPLOAD_BASE}/${p.photo}`) : null;
    const photoHtml = photoSrc ? `<img src="${photoSrc}" style="width:80px;height:80px;border-radius:50%;object-fit:cover;border:3px solid #003554;" onerror="this.style.display='none'"/>` : `<div style="width:80px;height:80px;border-radius:50%;background:#e9ecef;display:flex;align-items:center;justify-content:center;font-size:2rem;border:3px solid #dee2e6;">👤</div>`;
    w.document.write(`<!DOCTYPE html><html><head><title>Patient Card</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'Segoe UI',Arial,sans-serif;background:white;padding:16px;}
      .card{width:420px;border:2px solid #003554;border-radius:16px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.15);}
      .card-header{background:linear-gradient(135deg,#003554,#006494);color:white;padding:16px 20px;display:flex;align-items:center;gap:16px;}
      .clinic-info{font-size:0.78rem;opacity:0.8;}
      .card-body{padding:16px 20px;display:grid;grid-template-columns:1fr 1fr;gap:8px;}
      .field-label{font-size:0.68rem;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.5px;}
      .field-val{font-size:0.88rem;color:#222;font-weight:500;margin-top:1px;}
      .field-group{padding:4px 0;}
      .uhid-badge{display:inline-block;background:#003554;color:white;padding:3px 10px;border-radius:8px;font-size:0.78rem;font-weight:bold;margin-top:4px;letter-spacing:1px;}
      .card-footer{background:#f4f7f6;padding:8px 20px;font-size:0.72rem;color:#888;text-align:center;border-top:1px solid #e9ecef;}
      @media print{body{padding:0;}@page{size:90mm 55mm;margin:4mm;}}
    </style></head><body>
    <div class="card">
      <div class="card-header">
        ${photoHtml}
        <div>
          <div style="font-size:1.1rem;font-weight:bold;">${(p.title?p.title+' ':'')+p.name}</div>
          <div class="uhid-badge">${p.uhid}</div>
          <div class="clinic-info" style="margin-top:6px;">${clinic?.clinicName || 'Your Clinic'}</div>
        </div>
      </div>
      <div class="card-body">
        <div class="field-group"><div class="field-label">Phone</div><div class="field-val">${p.phone||'—'}</div></div>
        <div class="field-group"><div class="field-label">Gender</div><div class="field-val">${p.gender||'—'}</div></div>
        ${dob?`<div class="field-group"><div class="field-label">Date of Birth</div><div class="field-val">${dob}</div></div>`:''}
        <div class="field-group"><div class="field-label">Age</div><div class="field-val">${p.age||'—'} yrs</div></div>
        <div class="field-group"><div class="field-label">Patient Since</div><div class="field-val">${sinceDate}</div></div>
        ${p.address?`<div class="field-group" style="grid-column:1/-1;"><div class="field-label">Address</div><div class="field-val">${p.address}</div></div>`:''}
        ${p.insurance?.companyName?`<div class="field-group"><div class="field-label">Insurance</div><div class="field-val">${p.insurance.companyName}</div></div>`:''}
        ${p.medicalHistory?.length>0?`<div class="field-group" style="grid-column:1/-1;"><div class="field-label">Medical History</div><div class="field-val">${p.medicalHistory.join(', ')}</div></div>`:''}
      </div>
      <div class="card-footer">${clinic?.clinicName || 'Your Clinic'} &nbsp;•&nbsp; UHID: ${p.uhid}</div>
    </div>
    </body></html>`);
    w.document.close();
    setTimeout(()=>{ w.focus(); w.print(); }, 400);
  };

  // ── WhatsApp API-based auto-send ──
  const sendWhatsAppAPI = async (endpoint, data) => {
    try {
      const autoSettings = JSON.parse(localStorage.getItem('waAutoSettings') || '{}');
      // Check if auto-send is enabled for this type
      const typeMap = {
        'appointment-booked': 'autoAppointment',
        'treatment-done': 'autoTreatmentDone',
        'treatment-plan': 'autoTreatmentPlan',
        'payment-received': 'autoPayment',
      };
      const settingKey = typeMap[endpoint];
      if (settingKey && autoSettings[settingKey] === false) return; // disabled

      await axios.post(`${API_BASE}/whatsapp/send/${endpoint}`, data);
    } catch (e) {
      console.log('WhatsApp send failed:', e.message);
    }
  };

  const whatsappPatientSummary = (p, treatments, bills) => {
    // Keep wa.me link for manual summary (since it's user-initiated)
    if (!p?.phone) return alert('Phone number nahi hai!');
    const recentTreatments = (treatments||[]).slice(0,3);
    let msg = `🦷 *${clinic?.clinicName || 'Your Clinic'}*\nNamaste *${p.name}* ji! 🙏\n\n📋 *Patient ID:* ${p.uhid}\n`;
    if (recentTreatments.length > 0) {
      msg += `\n💉 *Recent Treatments:*\n`;
      recentTreatments.forEach(t => { msg += `• ${t.treatmentName}${t.toothNumber?' (#'+t.toothNumber+')':''} — ₹${t.totalAmount||0}\n`; });
    }
    msg += `\nThank you! 🦷${clinic?.clinicPhone ? '\nFor appointments: ' + clinic.clinicPhone : ''}`;
    const phone = p.phone.replace(/[^0-9]/g,'');
    const fullPhone = phone.startsWith('91') ? phone : '91'+phone;
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const whatsappTreatmentDone = (patient, treatmentName, toothNumber, medicines, instructions, amount) => {
    if (!patient?.phone) return;
    sendWhatsAppAPI('treatment-done', { to: patient.phone, patientName: patient.name, treatmentName, toothNumber, medicines, instructions, amount });
  };

  const whatsappPaymentReceived = (patient, amount, mode, voucherNo, outstanding) => {
    if (!patient?.phone) return;
    sendWhatsAppAPI('payment-received', { to: patient.phone, patientName: patient.name, amount, mode, voucherNo, outstanding });
  };

  const whatsappAppointmentBooked = (patientName, phone, date, time, treatment) => {
    if (!phone) return;
    const dateStr = new Date(date+'T00:00:00').toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long', year:'numeric'});
    sendWhatsAppAPI('appointment-booked', { to: phone, patientName, date: dateStr, time, treatment });
  };

  const whatsappTreatmentPlan = (patient, treatmentName, toothNumber, amount) => {
    if (!patient?.phone) return;
    sendWhatsAppAPI('treatment-plan', { to: patient.phone, patientName: patient.name, treatmentName, toothNumber, amount });
  };

  const handleDeleteSettingItem = async (id) => {
    await axios.delete(`${API_BASE}/settings/master-list/${id}`); fetchMasterLists();
  };

  const handleAddPlan = async () => {
    if (!newPlanName.trim()) return alert('Plan naam likho!');
    try { await axios.post(`${API_BASE}/settings/rate-plans`, { name: newPlanName }); setNewPlanName(''); fetchRatePlans(); }
    catch (e) { alert('Ye plan pehle se hai!'); }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm('Plan delete karna hai?')) return;
    await axios.delete(`${API_BASE}/settings/rate-plans/${id}`); fetchRatePlans();
  };

  const handleAddTreatment = async (planId) => {
    if (!newTreatment.name || !newTreatment.amount) return alert('Naam aur Amount dono zaruri hain!');
    try { await axios.post(`${API_BASE}/settings/rate-plans/${planId}/treatments`, newTreatment); setNewTreatment({ name: '', amount: '' }); fetchRatePlans(); }
    catch (e) { alert('Error!'); }
  };

  const handleDeleteTreatment = async (planId, tid) => {
    await axios.delete(`${API_BASE}/settings/rate-plans/${planId}/treatments/${tid}`); fetchRatePlans();
  };

  const handleUpdateTreatment = async (planId, tid) => {
    await axios.put(`${API_BASE}/settings/rate-plans/${planId}/treatments/${tid}`, editingTreatment);
    setEditingTreatment(null); fetchRatePlans();
  };

  // --- AUTH HELPERS ---
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${currentUser?.token}` } });

  const fetchUsersList = async () => {
    if (!currentUser || !['owner','admin','super_admin'].includes(currentUser.role)) return;
    try { const res = await axios.get(`${API_BASE}/tenant-users`, authHeaders()); setUsersList(res.data); } catch (e) {}
  };

  const handleLogin = async () => {
    setAuthLoading(true); setAuthMsg({ type: '', text: '' });
    try {
      const res = await axios.post(`${API_BASE}/saas-auth/login`, { email: authForm.username, password: authForm.password });
      const userData = { ...res.data.user, token: res.data.token };
      setCurrentUser(userData);
      localStorage.setItem('dentalUser', JSON.stringify(userData));
      setAuthForm({ username: '', password: '', fullName: '' });
    } catch (e) {
      setAuthMsg({ type: 'error', text: e.response?.data?.message || 'Login fail!' });
    }
    setAuthLoading(false);
  };

  const handleSignup = async () => {
    setAuthLoading(true); setAuthMsg({ type: '', text: '' });
    try {
      await axios.post(`${API_BASE}/saas-auth/signup`, authForm);
      setAuthMsg({ type: 'success', text: 'Signup ho gaya! Admin se approval lo.' });
      setAuthMode('login');
      setAuthForm({ username: '', password: '', fullName: '' });
    } catch (e) {
      setAuthMsg({ type: 'error', text: e.response?.data?.message || 'Signup fail!' });
    }
    setAuthLoading(false);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('dentalUser');
    sessionStorage.removeItem('oralzySkipLanding');
    sessionStorage.removeItem('oralzyAuthView');
    setShowAuth(false);
    setAuthView('login');
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserForm.username || !newUserForm.password || !newUserForm.fullName) return alert('Sab fields zaruri hain!');
    try {
      await axios.post(`${API_BASE}/tenant-users`, newUserForm, authHeaders());
      setNewUserForm({ username: '', password: '', fullName: '', role: 'staff' });
      fetchUsersList();
      alert('User ban gaya!');
    } catch (e) { alert(e.response?.data?.message || 'Error!'); }
  };

  const handleApproveUser = async (id, isApproved) => {
    try { await axios.patch(`${API_BASE}/tenant-users/${id}/approve`, { isApproved }, authHeaders()); fetchUsersList(); }
    catch (e) { alert('Error!'); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('User delete karna hai?')) return;
    try { await axios.delete(`${API_BASE}/tenant-users/${id}`, authHeaders()); fetchUsersList(); }
    catch (e) { alert(e.response?.data?.message || 'Error!'); }
  };

  const handleRoleChange = async (id, role) => {
    try { await axios.patch(`${API_BASE}/tenant-users/${id}/role`, { role }, authHeaders()); fetchUsersList(); }
    catch (e) { alert('Error!'); }
  };

  const handleChangePassword = async () => {
    if (!changePwForm.oldPassword || !changePwForm.newPassword) return alert('Dono fields zaruri hain!');
    try {
      await axios.post(`${API_BASE}/saas-auth/change-password`, {
        currentPassword: changePwForm.oldPassword,
        newPassword:     changePwForm.newPassword,
      }, authHeaders());
      setChangePwForm({ oldPassword: '', newPassword: '' });
      alert('Password badal gaya!');
    } catch (e) { alert(e.response?.data?.message || 'Error!'); }
  };

  const resetViews = () => { setSelectedPatient(null); setIsWritingPrescription(false); setIsWritingBill(false); setRegStep(1); };

  // --- MAIN UI RENDER ---

  // Everything below is wrapped by DeviceGate at the bottom return —
  // the gate decides whether to show admin-setup wizard / new-device flow / or pass through.
  const renderInner = () => {
    // Hard lock — backend identity changed. Refuse to operate until reset.
    if (syncMismatch) {
      return (
        <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#fef2f2', padding:24 }}>
          <div style={{ maxWidth:520, background:'white', borderRadius:14, border:'2px solid #fecaca', padding:28, boxShadow:'0 20px 60px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize:42, marginBottom:8 }}>🔒</div>
            <h2 style={{ margin:'0 0 8px', color:'#991b1b' }}>Different database detected</h2>
            <p style={{ color:'#7f1d1d', fontSize:14, lineHeight:1.55, margin:'0 0 14px' }}>
              This browser was previously connected to one Oralzy backend, but is now hitting a <strong>different one</strong>.
              To prevent the web app and mobile app from showing different data, the UI has been locked.
            </p>
            <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', padding:'10px 12px', borderRadius:8, fontSize:12.5, color:'#7f1d1d', marginBottom:14, fontFamily:'monospace' }}>
              <div>Previously seen: <strong>{(syncMismatch.stored||'').slice(0,12)}…</strong></div>
              <div>Now seeing:      <strong>{(syncMismatch.current||'').slice(0,12)}…</strong></div>
            </div>
            <p style={{ color:'#555', fontSize:12.5, margin:'0 0 16px' }}>
              If this is intentional (you migrated servers), click below to forget the old one. Otherwise check that the API URL is correct.
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => { resetStoredInstallId(); window.location.reload(); }}
                style={{ padding:'10px 18px', background:'#dc2626', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:13.5 }}>
                Trust new server &amp; reload
              </button>
              <button onClick={() => window.location.reload()}
                style={{ padding:'10px 18px', background:'white', color:'#991b1b', border:'1px solid #fecaca', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13.5 }}>
                Just reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    // V2 extracted 2026-04-24 — entire V2 app now lives at
    // ~/Desktop/ORALZY-V2-standalone/. Any request to /v2* now falls through
    // to the regular V1 app (or shows a friendly 404-style redirect below).
    if (typeof window !== 'undefined' && /^\/v2(\/|$)/.test(window.location.pathname)) {
      // Silently redirect back to root so stale /v2 links don't 500.
      window.history.replaceState({}, '', '/');
    }

    // Public legal pages for Play Store / App Store review + DPDP compliance.
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      if (p === '/privacy' || p === '/privacy-policy') {
        const { PrivacyPage } = require('./pages/LegalPage');
        return <PrivacyPage/>;
      }
      if (p === '/terms' || p === '/terms-of-service') {
        const { TermsPage } = require('./pages/LegalPage');
        return <TermsPage/>;
      }
    }

    // Phase 10 — public patient portal route (no login required). Bypasses app shell.
    if (typeof window !== 'undefined' && /^\/patient\/[a-f0-9]+/.test(window.location.pathname)) {
      return <PatientPortalPage API_BASE={API_BASE}/>;
    }

    // E2 — public online booking page at /book/:clinicSlug (no auth required)
    if (typeof window !== 'undefined' && /^\/book\//.test(window.location.pathname)) {
      return <PublicBookingPage />;
    }

    // Lab portal — public, token-gated page where external labs chat with the
    // clinic, download IOS scans, and hit Accepted / Working / Finishing / Sent.
    if (typeof window !== 'undefined' && /^\/lab-portal\//.test(window.location.pathname)) {
      return <LabPortalPage/>;
    }

    // Full-blown Lab App — login + signup + dashboard with chat + status
    // buttons. Completely separate identity from clinic staff (LabUser
    // accounts, not User). Clinic verifies each lab before cases show.
    if (typeof window !== 'undefined' && /^\/lab-app(\/|$)/.test(window.location.pathname)) {
      return <LabAppPage/>;
    }

    // Landing page → Clinic Signup / Login → App shell
    if (!currentUser) {
      if (!showAuth && authView !== 'clinic-signup') {
        // Static landing page in public/index.html handles first-time visitors.
        // React renders nothing until a CTA there dispatches `oralzy-start`.
        return null;
      }
      if (authView === 'clinic-signup') {
        return <ClinicSignupPage
          API_BASE={API_BASE}
          onSignupSuccess={() => setAuthView('login')}
          onBackToLogin={() => setAuthView('login')}
        />;
      }
      return <LoginPage
        API_BASE={API_BASE}
        onLogin={(u)=>{
          sessionStorage.removeItem('oralzySkipLanding');
          sessionStorage.removeItem('oralzyAuthView');
          setCurrentUser(u);
          if (window.location.pathname === '/login' || window.location.pathname === '/signup' || window.location.pathname === '/') {
            window.history.pushState({}, '', '/app');
          }
        }}
        onClinicSignup={() => setAuthView('clinic-signup')}
      />;
    }
    // Super admin gets a dedicated platform-management UI — no clinic sidebar,
    // no patient/calendar/etc. Those are clinic-owner features. Super admin
    // impersonates a clinic when they need to see its data.
    if (currentUser?.role === 'super_admin') {
      return (
        <div style={{ minHeight: '100vh', background: theme.bg, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, maxWidth: 1100, margin: '0 auto 20px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.4rem' }}>🛡 ORALZY Super Admin</h1>
              <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: 4 }}>
                Platform owner — {currentUser.fullName} · {currentUser.username}
              </div>
            </div>
            <button onClick={()=>{ localStorage.clear(); setCurrentUser(null); }}
              style={{ padding: '8px 16px', background: theme.danger, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              Logout
            </button>
          </div>
          <SuperAdminPage API_BASE={API_BASE} token={localStorage.getItem('dentalToken')} />
        </div>
      );
    }
    return renderAppShell();
  };

  const currentNavLabel = PAGE_LABELS[activeTab] || 'Dashboard';

  const renderAppShell = () => (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: theme.bg }}>
      {/* Sidebar — hidden in pharmacy mode. The .sidebar-mobile-open
          className lets index.css render it as a fixed drawer on phones. */}
      {!pharmacyMode && (
      <div className={mobileMenuOpen ? 'sidebar-mobile-open' : ''}>
      <Sidebar
        activeTab={activeTab}
        setActiveTab={(t) => { setActiveTab(t); setMobileMenuOpen(false); }}
        resetViews={resetViews}
        currentUser={currentUser}
        handleLogout={handleLogout}
        onSettingsOpen={()=>{ if (['owner','admin','super_admin'].includes(currentUser?.role)) fetchUsersList(); }}
        collapsed={mobileMenuOpen ? false : sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      </div>
      )}
      {/* Mobile drawer backdrop — tap-to-close. Desktop never sees it
          because the hamburger never appears > 768px. */}
      {mobileMenuOpen && (
        <div onClick={() => setMobileMenuOpen(false)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:999}}/>
      )}

      {/* ─── Main column ─── */}
      <div style={{flex:1, display:'flex', flexDirection:'column', minWidth:0}}>
        <Header pageTitle={pharmacyMode ? 'Pharmacy' : currentNavLabel} onSearch={pharmacyMode ? undefined : ()=>setShowGlobalSearch(true)} onNewPatient={pharmacyMode ? undefined : ()=>{setActiveTab('register'); setRegStep(1);}} onFaceCheckin={pharmacyMode ? undefined : ()=>setShowFaceCheckin(true)} onMenuClick={pharmacyMode ? undefined : ()=>setMobileMenuOpen(true)} pharmacyMode={pharmacyMode} setPharmacyMode={setPharmacyMode} pharmacyEnabled={pharmacyEnabled}/>

      {/* Pharmacy Mode — full screen */}
      {pharmacyMode ? (
        <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
          <Suspense fallback={<PageLoading/>}>
            <PharmacyPage API_BASE={API_BASE} patientsList={patientsList}/>
          </Suspense>
        </div>
      ) : (
      <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
      <Suspense fallback={<PageLoading/>}>
        {/* DASHBOARD */}
        {activeTab === 'dashboard' && (
          <DashboardPage
            currentUser={currentUser}
            dashboardStats={dashboardStats}
            patientsList={patientsList}
            API_BASE={API_BASE}
            onNavigate={setActiveTab}
          />
        )}

        {/* CALENDAR */}
        {activeTab === 'calendar' && (
          <CalendarPage app={{
            API_BASE,
            appointmentsList, fetchAppointments,
            selAppt, setSelAppt,
            calView, setCalView,
            calDayDate, setCalDayDate,
            calWeekStart, setCalWeekStart,
            weekStartDay,
            calMonth, setCalMonth, calYear, setCalYear,
            selectedDate, setSelectedDate,
            setSlotClick,
            setCalBookForm,
            patientsList, treatmentTypes,
            branches,
          }}/>
        )}

        {/* APPOINTMENTS */}
        {activeTab === 'appointments' && (
          <AppointmentsPage
            appointmentsList={appointmentsList}
            appointmentForm={appointmentForm}
            setAppointmentForm={setAppointmentForm}
            handleBookAppointment={handleBookAppointment}
          />
        )}

        {/* REGISTER */}
        {activeTab === 'register' && (
          <RegisterPatientPage app={{
            regStep, setRegStep,
            patient, setPatient,
            capturedPhoto, showWebcam,
            videoRef, startWebcam, capturePhoto, stopWebcam, retakePhoto,
            handleSavePatient,
            medicalList, dentalList, medicinesList, allergiesList,
            ratePlans, groupsList, tagsList,
            regFeeConfig,
          }}/>
        )}

        {/* PATIENTS (DIRECTORY + PROFILE) */}
        {activeTab === 'list' && (
          <PatientsPage app={{
            API_BASE, UPLOAD_BASE,
            patientsList, searchTerm, setSearchTerm,
            selectedPatient, setSelectedPatient, resetViews,
            pastTreatments, pastBills, pastPrescriptions,
            patientReports, fetchPastPrescriptions,
            patientTab, setPatientTab,
            isWritingPrescription, setIsWritingPrescription,
            isWritingBill, setIsWritingBill,
            chiefComplaintList, findingsList, investigationList, diagnosisList,
            billForm, setBillForm, currentBillItem, setCurrentBillItem, billTotal,
            handleAddBillItem, handleSaveBill,
            ratePlans,
            treatmentTypes, instructionList, medicineLibrary,
            reportTitle, setReportTitle, reportFile, setReportFile, handleFileUpload,
            printPatientCard, whatsappPatientSummary, whatsappTreatmentDone, whatsappPaymentReceived, whatsappTreatmentPlan,
            fetchAllPatients,
            medicalList, dentalList, medicinesList, allergiesList, groupsList, tagsList,
            setShowRegForm,
          }}/>
        )}

        {/* SETTINGS */}
        {activeTab === 'settings' && (
          <SettingsPage app={{
            API_BASE,
            branches, refreshBranches, refreshDoctors,
            openSettingSection, setOpenSettingSection,
            weekStartDay, setWeekStartDay, setCalWeekStart,
            calView, setCalView,
            currentUser,
            usersList, newUserForm, setNewUserForm,
            handleCreateUser, handleApproveUser, handleRoleChange, handleDeleteUser,
            changePwForm, setChangePwForm, handleChangePassword,
            medicineLibrary, newMedLib, setNewMedLib, fetchMasterLists,
            instructionList, newSettingItem, setNewSettingItem, handleAddSettingItem, handleDeleteSettingItem,
            treatmentTypes, newTreatmentType, setNewTreatmentType, fetchTreatmentTypes,
            editingTT, setEditingTT,
            chiefComplaintList, findingsList, investigationList, diagnosisList,
            groupsList, tagsList, medicalList, dentalList, medicinesList, allergiesList,
            ratePlans, newPlanName, setNewPlanName, handleAddPlan, handleDeletePlan,
            newTreatment, setNewTreatment, editingTreatment, setEditingTreatment,
            handleAddTreatment, handleUpdateTreatment, handleDeleteTreatment,
          }} onSwitchTab={(tab)=>setActiveTab(tab)}/>
        )}

        {/* ── ANALYTICS / REPORTS ── */}
        {/* RECALLS — recare-campaigns legacy key opens RecallPage on AI Auto tab */}
        {activeTab === 'recalls' && <RecallPage API_BASE={API_BASE} patientsList={patientsList}/>}
        {activeTab === 'recare-campaigns' && <RecallPage API_BASE={API_BASE} patientsList={patientsList} initialTab="recare-campaigns"/>}

        {/* CALLING MODE — redirected into TelephonyHubPage */}
        {activeTab === 'calling-mode' && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="calling-mode"/>}

        {/* INVENTORY */}
        {activeTab === 'inventory' && (
          <InventoryPage API_BASE={API_BASE}/>
        )}

        {/* LAB ORDERS */}
        {activeTab === 'lab' && (
          <LabOrdersPage API_BASE={API_BASE} patientsList={patientsList}/>
        )}

        {/* EXPENSES — analyzer is now a tab inside ExpensesPage */}
        {activeTab === 'expenses' && <ExpensesPage API_BASE={API_BASE}/>}

        {/* PACKAGES */}
        {activeTab === 'packages' && (
          <PackagesPage API_BASE={API_BASE} patientsList={patientsList}/>
        )}

        {/* INSURANCE — verify is now a tab inside InsurancePage */}
        {activeTab === 'insurance' && <InsurancePage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser}/>}

        {/* ONLINE BOOKINGS */}
        {/* BOOKINGS — wait-list is now a tab inside OnlineBookingsPage */}
        {activeTab === 'bookings' && (
          <OnlineBookingsPage API_BASE={API_BASE}/>
        )}

        {/* MARKETING */}
        {activeTab === 'marketing' && (
          <MarketingPage API_BASE={API_BASE}/>
        )}

        {/* AUDIT LOG */}
        {/* EXPENSE ANALYZER — redirected into ExpensesPage Analyzer tab */}
        {activeTab === 'analyzer' && <ExpensesPage API_BASE={API_BASE} initialTab="analyzer"/>}

        {/* PHARMACY */}
        {activeTab === 'pharmacy' && (
          <PharmacyPage API_BASE={API_BASE} patientsList={patientsList}/>
        )}

        {/* WhatsApp hub — 11 legacy keys redirect into WhatsAppHubPage tabs */}
        {activeTab === 'whatsapp'             && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="whatsapp"/>}
        {activeTab === 'wa-contacts'          && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-contacts"/>}
        {activeTab === 'wa-templates'         && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-templates"/>}
        {activeTab === 'wa-campaigns'         && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-campaigns"/>}
        {activeTab === 'wa-autoreply'         && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-autoreply"/>}
        {activeTab === 'wa-inbox'             && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-inbox"/>}
        {activeTab === 'wa-flows'             && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-flows"/>}
        {activeTab === 'wa-forms'             && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-forms"/>}
        {activeTab === 'wa-api-keys'          && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-api-keys"/>}
        {activeTab === 'wa-interactive-flows' && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-interactive-flows"/>}
        {activeTab === 'wa-analytics'         && <WhatsAppHubPage API_BASE={API_BASE} patientsList={patientsList} initialTab="wa-analytics"/>}

        {activeTab === 'admin' && (
          <AuditLogPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'help' && (
          <HelpPage onNavigate={(tab)=>setActiveTab(tab)}/>
        )}

        {activeTab === 'action-guard' && (
          <ActionGuardPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'sop-editor' && (
          <SOPEditorPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'manuals' && (
          <EditableDocsPage API_BASE={API_BASE} currentUser={currentUser}
            type="manual" label="Manuals" icon="📖"
            description="Clinic manuals — editable with revision history + print" />
        )}

        {activeTab === 'policies' && (
          <EditableDocsPage API_BASE={API_BASE} currentUser={currentUser}
            type="policy" label="Policies" icon="📜"
            description="Clinic policies — editable with revision history + print" />
        )}

        {activeTab === 'organogram' && (
          <EditableDocsPage API_BASE={API_BASE} currentUser={currentUser}
            type="organogram" label="Organogram" icon="🧩"
            description="Organisational hierarchy chart — editable + print" />
        )}

        {activeTab === 'staff' && (
          <StaffDirectoryPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'staff-attendance' && (
          <StaffAttendancePage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'hr-masters' && (
          <HRMasterPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'patient-referrals' && (
          <PatientReferralsPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'doctor-commissions' && (
          <DoctorCommissionsPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'franchise' && (
          <FranchisePage API_BASE={API_BASE}/>
        )}

        {activeTab === 'clinical-quality' && (
          <ClinicalQualityPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'smart-schedule' && (
          <SmartSchedulePage API_BASE={API_BASE}/>
        )}

        {activeTab === 'risk-scores' && (
          <RiskScoresPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'dental-chart' && (
          <DentalChartPage API_BASE={API_BASE}/>
        )}

        {/* Imaging hub — Radiographs + AI Imaging + DSLR consolidated */}
        {activeTab === 'radiographs' && <ImagingHubPage API_BASE={API_BASE} initialTab="radiographs"/>}

        {activeTab === 'gift-cards' && (
          <GiftCardsPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'coupon-manager' && (
          <CouponManagerPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'inventory-forecast' && (
          <InventoryForecastPage API_BASE={API_BASE}/>
        )}

        {/* wait-list redirects into OnlineBookingsPage Wait List tab */}
        {activeTab === 'wait-list' && <OnlineBookingsPage API_BASE={API_BASE} initialTab="wait-list"/>}

        {/* Legacy pharmacy-smart route now lands on PharmacyPage Smart tab. */}
        {activeTab === 'pharmacy-smart' && (
          <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="pharmacy-smart"/>
        )}

        {activeTab === 'supplier-scorecard' && (
          <SupplierScorecardPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'patient-ltv' && (
          <PatientLTVPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'treatment-outcomes' && (
          <TreatmentOutcomesPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'executive-dashboard' && (
          <ExecutiveDashboardPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'clinical-protocols' && (
          <ClinicalProtocolsPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'vital-signs' && (
          <VitalSignsPage API_BASE={API_BASE}/>
        )}

        {activeTab === 'fraud-detection' && <FraudDetectionPage API_BASE={API_BASE}/>}
        {activeTab === 'dynamic-pricing' && <DynamicPricingPage API_BASE={API_BASE}/>}
        {activeTab === 'literature-search' && <LiteratureSearchPage API_BASE={API_BASE}/>}
        {activeTab === 'scanner-import' && <ScannerImportPage API_BASE={API_BASE} patientsList={patientsList}/>}

        {activeTab === 'staff-checkin' && (
          <StaffCheckInPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'sop-library' && (
          <SOPLibraryPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {/* CREDENTIALS hub — Certificates + Training merged */}
        {activeTab === 'credentials'    && <CredentialsHubPage API_BASE={API_BASE} currentUser={currentUser}/>}
        {activeTab === 'certificates'   && <CredentialsHubPage API_BASE={API_BASE} currentUser={currentUser} initialTab="certificates"/>}

        {activeTab === 'backups' && (
          <BackupsPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'kpi' && (
          <KPIRegisterPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {/* Legacy routes — both old standalone pages now land on NABHPage
            with the matching initial tab selected. */}
        {(activeTab === 'nabh-compliance' || activeTab === 'nabh-scorecard') && (
          <NABHPage API_BASE={API_BASE} authHeaders={authHeaders} currentUser={currentUser} initialTab={activeTab}/>
        )}

        {activeTab === 'committees' && (
          <CommitteeMeetingsPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'mock-audit' && (
          <MockAuditPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'patient-safety' && (
          <PatientSafetyPage API_BASE={API_BASE} currentUser={currentUser} patientsList={patientsList}/>
        )}

        {activeTab === 'dispensing-register' && (
          <DispensingRegisterPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'treatment-complete' && (
          <TreatmentCompletionPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'inspector-package' && (
          <InspectorPackagePage API_BASE={API_BASE}/>
        )}

        {activeTab === 'retention' && (
          <RetentionPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'alerts' && (
          <AlertsPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'huddle' && (
          <HuddlePage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'reviews' && (
          <ReviewsPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'forms' && (
          <FormBuilderPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'checkin' && (
          <CheckinQueuePage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'chat' && (
          <ChatPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'insurance-verify' && <InsurancePage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="insurance-verify"/>}

        {activeTab === 'data-migration' && (
          <DataMigrationPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'ai-insights' && (
          <AIInsightsPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'veeransh-training' && (
          <VeeranshTrainingPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'india-compliance' && (
          <IndiaCompliancePage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'premium' && (
          <PremiumPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'clinical' && (
          <ClinicalPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'registers' && (
          <RegistersPage API_BASE={API_BASE} currentUser={currentUser}/>
        )}

        {activeTab === 'audit-prep' && (
          <AuditPrepPage API_BASE={API_BASE} onNavigate={setActiveTab}/>
        )}

        {/* CHECKLISTS hub — Tasks + Onboarding merged */}
        {activeTab === 'checklists'        && <ChecklistsHubPage API_BASE={API_BASE} currentUser={currentUser}/>}
        {activeTab === 'staff-tasks'       && <ChecklistsHubPage API_BASE={API_BASE} currentUser={currentUser} initialTab="staff-tasks"/>}
        {activeTab === 'staff-onboarding'  && <ChecklistsHubPage API_BASE={API_BASE} currentUser={currentUser} initialTab="staff-onboarding"/>}

        {activeTab === 'compliance' && (
          <CompliancePage API_BASE={API_BASE} authHeaders={authHeaders} onNavigate={setActiveTab} />
        )}

        {activeTab === 'security' && (
          <SecurityPage API_BASE={API_BASE} />
        )}

        {/* Compliance Logs — sterilization, BMW, adverse events, equipment maintenance */}
        {activeTab === 'compliance-logs' && <ComplianceLogsPage API_BASE={API_BASE}/>}

        {/* Phase 2 — Treatment EMI installment plans */}
        {activeTab === 'emi-plans' && <EMIPlansPage API_BASE={API_BASE} patientsList={patientsList}/>}

        {/* Phase 2 — Treatment-type profitability report */}
        {activeTab === 'profitability' && <ProfitabilityReportPage API_BASE={API_BASE}/>}

        {/* Phase 4 — Government scheme empanelment + claims */}
        {activeTab === 'gov-schemes' && <GovSchemesPage API_BASE={API_BASE} patientsList={patientsList}/>}

        {activeTab === 'billing' && (
          <BillingPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} />
        )}

        {activeTab === 'saas-billing' && (
          <SaasBillingPage API_BASE={API_BASE} />
        )}

        {activeTab === 'financial' && (
          <FinancialControlsPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} />
        )}

        {/* Legacy pharmacy-advanced route now lands on PharmacyPage Supply tab. */}
        {activeTab === 'pharmacy-advanced' && (
          <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="pharmacy-advanced"/>
        )}

        {activeTab === 'tax' && (
          <TaxCompliancePage API_BASE={API_BASE} currentUser={currentUser} />
        )}

        {activeTab === 'day-end' && (
          <DayEndPage API_BASE={API_BASE} currentUser={currentUser} />
        )}

        {activeTab === 'analytics-hub' && (
          <AnalyticsHubPage API_BASE={API_BASE} />
        )}

        {activeTab === 'integrations-hub' && (
          <IntegrationsHubPage API_BASE={API_BASE} currentUser={currentUser} patientsList={patientsList} />
        )}

        {/* Telephony hub — 6 keys redirect into TelephonyHubPage tabs */}
        {activeTab === 'telephony'       && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser}/>}
        {activeTab === 'call-log'        && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="call-log"/>}
        {activeTab === 'tracked-numbers' && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="tracked-numbers"/>}
        {activeTab === 'frontdesk-inbox' && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="frontdesk-inbox"/>}
        {/* recare-campaigns route now handled above (redirects into RecallPage AI Auto tab) */}
        {activeTab === 'telemed'         && <TeleMedPage API_BASE={API_BASE}/>}
        {activeTab === 'health-score'    && <HealthScorePage API_BASE={API_BASE}/>}
        {activeTab === 'fax'             && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="fax"/>}
        {activeTab === 'voip'            && <TelephonyHubPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="voip"/>}

        {/* ── HUB PAGES (11-item sidebar) ── */}
        {activeTab === 'clinical-hub'    && <ClinicalHubPage  onNavigate={setActiveTab}/>}
        {activeTab === 'billing-hub'     && <BillingHubPage   onNavigate={setActiveTab}/>}
        {activeTab === 'pharmacy-hub'    && <PharmacyHubPage  onNavigate={setActiveTab}/>}
        {activeTab === 'staff-hub'       && <StaffHubPage     onNavigate={setActiveTab}/>}
        {activeTab === 'marketing-hub'   && <MarketingHubPage onNavigate={setActiveTab}/>}

        {/* HR Pages */}
        {/* Payroll consolidated hub — 4 legacy keys redirect into PayrollHubPage tabs */}
        {activeTab === 'payroll'         && <PayrollHubPage API_BASE={API_BASE}/>}
        {activeTab === 'staff-leaves'    && <PayrollHubPage API_BASE={API_BASE} initialTab="staff-leaves"/>}
        {activeTab === 'payslips'        && <PayrollHubPage API_BASE={API_BASE} initialTab="payslips"/>}
        {activeTab === 'incentives'      && <PayrollHubPage API_BASE={API_BASE} initialTab="incentives"/>}
        {activeTab === 'staff-advances'  && <PayrollHubPage API_BASE={API_BASE} initialTab="staff-advances"/>}
        {activeTab === 'staff-training'  && <CredentialsHubPage API_BASE={API_BASE} currentUser={currentUser} initialTab="staff-training"/>}

        {/* Finance Pages */}
        {activeTab === 'estimates'       && <EstimatesPage API_BASE={API_BASE}/>}
        {activeTab === 'ledger'          && <LedgerPage API_BASE={API_BASE}/>}
        {activeTab === 'credit-notes'    && <CreditNotesPage API_BASE={API_BASE}/>}
        {activeTab === 'coupons'         && <CouponManagerPage API_BASE={API_BASE} currentUser={currentUser}/>}
        {activeTab === 'recurring-charges' && <RecurringChargesPage API_BASE={API_BASE}/>}

        {/* Legacy inventory routes — all land on PharmacyPage Supply Chain tab */}
        {activeTab === 'purchase-orders' && <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="purchase-orders"/>}
        {activeTab === 'grns'            && <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="grns"/>}
        {activeTab === 'stock-adjustments' && <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="stock-adjustments"/>}
        {activeTab === 'stock-transfers' && <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="stock-transfers"/>}
        {activeTab === 'pharmacy-suppliers' && <PharmacyPage API_BASE={API_BASE} patientsList={patientsList} currentUser={currentUser} initialTab="pharmacy-suppliers"/>}

        {/* Clinical Pages — ortho/perio/implants/education redirect into ClinicalPage tabs */}
        {activeTab === 'prescriptions'   && <PrescriptionsPage API_BASE={API_BASE}/>}
        {activeTab === 'ortho'           && <ClinicalPage API_BASE={API_BASE} currentUser={currentUser} initialTab="ortho"/>}
        {activeTab === 'perio'           && <ClinicalPage API_BASE={API_BASE} currentUser={currentUser} initialTab="perio"/>}
        {activeTab === 'implants'        && <ClinicalPage API_BASE={API_BASE} currentUser={currentUser} initialTab="implants"/>}
        {activeTab === 'education'       && <ClinicalPage API_BASE={API_BASE} currentUser={currentUser} initialTab="education"/>}

        {/* Advanced Pages */}
        {activeTab === 'leads'           && <LeadsPage API_BASE={API_BASE}/>}
        {activeTab === 'ai-imaging'      && <ImagingHubPage API_BASE={API_BASE} initialTab="ai-imaging"/>}
        {activeTab === 'dslr'            && <ImagingHubPage API_BASE={API_BASE} initialTab="dslr"/>}
        {activeTab === 'onedrive-backup' && <OneDriveBackupPage API_BASE={API_BASE}/>}
        {activeTab === 'supplier-analytics' && <SupplierAnalyticsPage API_BASE={API_BASE}/>}

        {activeTab === 'analytics' && (
          <ReportsPage
            reportCategory={reportCategory} setReportCategory={setReportCategory}
            reportSubReport={reportSubReport} setReportSubReport={setReportSubReport}
            reportData={reportData} setReportData={setReportData}
            reportLoadingSub={reportLoadingSub}
            analyticsDateFrom={analyticsDateFrom} setAnalyticsDateFrom={setAnalyticsDateFrom}
            analyticsDateTo={analyticsDateTo} setAnalyticsDateTo={setAnalyticsDateTo}
            patientsList={patientsList}
            fetchSubReport={fetchSubReport}
          />
        )}

      </Suspense>
      </div>
      )}
      </div>

      {showGlobalSearch && (
        <GlobalSearch
          API_BASE={API_BASE}
          onSelectPatient={(pt) => { setActiveTab('list'); setSelectedPatient(pt); }}
          onNavigate={(tab) => setActiveTab(tab)}
          onClose={() => setShowGlobalSearch(false)}
        />
      )}

      <LiveCallPopup
        API_BASE={API_BASE}
        onSelectPatient={(pt) => { setActiveTab('list'); setSelectedPatient(pt); }}
        onRegisterUnknown={(phone) => { setActiveTab('register'); }}
      />

      {/* SSE-driven popup fed by mobile call detector. Different from
          LiveCallPopup (which polls /voip/live). Mobile native code
          POSTs /api/incoming-calls/detect → server fans event here. */}
      <IncomingCallPopup
        onSelectPatient={(pt) => { setActiveTab('list'); setSelectedPatient(pt); }}
        onRegisterUnknown={(phone) => { setActiveTab('register'); }}
      />

      {showRegForm && (
        <RegistrationFormPrint
          patient={showRegForm}
          clinic={{
            name: (clinic?.clinicName || 'Your Clinic').toUpperCase(),
            address: clinic?.clinicAddress || '',
            phone: clinic?.clinicPhone || '',
          }}
          onClose={() => { setShowRegForm(null); }}
        />
      )}

      {showFaceCheckin && (
        <FaceCheckin
          API_BASE={API_BASE}
          UPLOAD_BASE={UPLOAD_BASE}
          onPatientFound={(pt) => {
            setShowFaceCheckin(false);
            setActiveTab('list');
            setSelectedPatient(pt);
          }}
          onClose={()=>setShowFaceCheckin(false)}
        />
      )}

      <SlotBookingModal
        API_BASE={API_BASE}
        slotClick={slotClick}
        setSlotClick={setSlotClick}
        calBookForm={calBookForm}
        setCalBookForm={setCalBookForm}
        handleCalBook={handleCalBook}
        onNewPatient={()=>{ setActiveTab('register'); setRegStep(1); }}
        patientsList={patientsList}
        treatmentTypes={treatmentTypes}
      />

      {/* Global Action Guard prompt — stays mounted so any page's axios call
          that triggers ACTION_GUARD_REQUIRED can surface the same modal. */}
      <ActionGuardPrompt API_BASE={API_BASE}/>

      {/* ─── VEERANSH AI FLOATING BUTTON ─── */}
      <div
        onClick={() => setShowVeeransh(!showVeeransh)}
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
          width: 60, height: 60, borderRadius: '50%',
          background: showVeeransh
            ? `linear-gradient(135deg, ${theme.danger}, #dc2626)`
            : `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: showVeeransh
            ? `0 4px 20px ${theme.danger}50`
            : `0 4px 20px ${theme.primary}50, 0 0 40px ${theme.primary}20`,
          transition: 'all 0.3s ease',
          animation: showVeeransh ? 'none' : 'veeranshFloat 3s ease-in-out infinite',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        title={showVeeransh ? 'Close Veeransh' : 'Veeransh - Clinic & General Intelligence'}
      >
        {showVeeransh
          ? <span style={{ color: '#fff', fontSize: 24, fontWeight: 700, lineHeight: 1 }}>&#x2715;</span>
          : <Brain size={28} color="#fff" />
        }
        {!showVeeransh && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            width: 14, height: 14, borderRadius: '50%',
            background: theme.success, border: '2px solid #fff',
            animation: 'veeranshPulse 2s ease-in-out infinite',
          }} />
        )}
      </div>

      {/* ─── VEERANSH AI PANEL ─── */}
      {showVeeransh && (
        <div style={{
          position: 'fixed', bottom: 100, right: 28, zIndex: 9998,
          width: 420, height: 'calc(100vh - 140px)', maxHeight: 700,
          borderRadius: 20, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 0 40px rgba(0,181,184,0.1)',
          border: `1px solid ${theme.border}`,
          animation: 'veeranshSlideUp 0.3s ease-out',
          backgroundColor: '#ffffff',
        }}>
          <VeeranshAI
            API_BASE={API_BASE}
            theme={theme}
            token={currentUser?.token || localStorage.getItem('dentalToken')}
            errorContext={veeranshErrorContext}
            onClose={() => { setShowVeeransh(false); setVeeranshErrorContext(null); }}
          />
        </div>
      )}

      <style>{`
        @keyframes veeranshFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        @keyframes veeranshPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.3); } }
        @keyframes veeranshSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

    </div>
  );

  // Skip DeviceGate for the public surface — landing page, public booking,
  // anything before login. These pages have zero secret data, and the gate's
  // server round-trip turns a "fast marketing site" into a 5-30s wait on
  // cold starts (Render free tier). DeviceGate kicks in only once the user
  // chooses to authenticate.
  const isPublicSurface =
    !currentUser &&
    !showAuth &&
    authView !== 'clinic-signup' &&
    !window.location.pathname.startsWith('/book/');
  if (isPublicSurface) return renderInner();

  return <DeviceGate API_BASE={API_BASE}>{renderInner()}</DeviceGate>;
}

// Wrap with Branch + Clinic providers so every page can read the active
// branch/doctor list AND the clinic's identity (for print headers, etc.)
function App() {
  const API_BASE = API_BASE_ENV || (IS_PRODUCTION ? '/api' : `http://${IP_ADDRESS}:5000/api`);
  return (
    <ClinicProvider API_BASE={API_BASE}>
      <BranchProvider API_BASE={API_BASE}>
        <AppInner/>
      </BranchProvider>
    </ClinicProvider>
  );
}

export default App;
